import React from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Alert, Box, IconButton } from "@mui/material";
import { lighten } from "@mui/system";
import { useTheme } from "@mui/material/styles";
import { ConfirmationModal } from "@packsize/components";
import { useToast } from "@packsize/toast";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

import { type MachineGroup } from "@/schemas";
import { useDeleteMachineGroup } from "@/hooks/machine-groups";

interface MachineGroupDeleteButtonProps {
	readonly machineGroup: MachineGroup;
}

export const MachineGroupDeleteButton: React.FC<MachineGroupDeleteButtonProps> = ({ machineGroup }) => {
	const intl = useIntl();
	const theme = useTheme();
	const { toastSuccess, toastError } = useToast();

	const [showDeleteConfirmation, setShowDeleteConfirmation] = React.useState(false);
	const [isDisabled, setIsDisabled] = React.useState(false);
	const [showOpenWorkError, setShowOpenWorkError] = React.useState(false);

	const { mutate: deleteMachineGroup, isPending } = useDeleteMachineGroup();

	const handleDeleteConfirmed = (): void => {
		deleteMachineGroup(machineGroup.id, {
			onSuccess: (result) => {
				if (!result.deleted) {
					setShowOpenWorkError(true);
					setIsDisabled(true);
					return;
				}

				setShowDeleteConfirmation(false);
				setShowOpenWorkError(false);
				setIsDisabled(false);

				toastSuccess({
					message: intl.formatMessage({
						id: "Machine group deleted successfully",
						defaultMessage: "Machine group deleted successfully",
					}),
				});
			},
			onError: () => {
				toastError({
					message: intl.formatMessage({
						id: "Failed to delete machine group",
						defaultMessage: "Failed to delete machine group",
					}),
				});

				setShowDeleteConfirmation(false);
				setShowOpenWorkError(false);
				setIsDisabled(false);
			},
		}).catch(() => {
			/* Handled via onError above; caught here to avoid an unhandled rejection since mutate is mutateAsync. */
		});
	};

	const handleClose = (): void => {
		setShowDeleteConfirmation(false);
		setShowOpenWorkError(false);
		setIsDisabled(false);
	};

	return (
		<>
			<ConfirmationModal
				open={showDeleteConfirmation}
				onClose={handleClose}
				onCancel={handleClose}
				onConfirm={handleDeleteConfirmed}
				title={intl.formatMessage({ id: "Delete machine group", defaultMessage: "Delete machine group" })}
				confirmText={intl.formatMessage({ id: "Delete", defaultMessage: "Delete" })}
				cancelText={intl.formatMessage({ id: "Cancel", defaultMessage: "Cancel" })}
				slotProps={{
					confirmButton: {
						disabled: isDisabled,
						loading: isPending,
						"data-testid": "machine-group-delete-confirmation-modal__button__confirm",
					},
					cancelButton: {
						"data-testid": "machine-group-delete-confirmation-modal__button__cancel",
					},
				}}
			>
				<Box>
					{intl.formatMessage({
						id: "This change can not be undone.",
						defaultMessage: "This change can not be undone.",
					})}
				</Box>
				{showOpenWorkError === true && (
					<Box marginTop={2}>
						<Alert severity="error" sx={{ backgroundColor: lighten(theme.palette.error.main, 0.9) }}>
							<FormattedMessage id="Before you can delete" defaultMessage="Before you can delete" />
							<ul>
								<li>
									<FormattedMessage
										id="There must not be pending work assigned to {itemName}"
										defaultMessage="There must not be pending work assigned to {itemName}"
										values={{ itemName: machineGroup.alias }}
									/>
								</li>
							</ul>
						</Alert>
					</Box>
				)}
			</ConfirmationModal>
			<IconButton
				onClick={() => setShowDeleteConfirmation(true)}
				data-testid="machine-group-delete-button__button__open-modal"
				data-cy="delete-button"
				data-pendoid="machine-groups__button__delete"
			>
				<DeleteOutlineIcon sx={{ fontSize: 24, color: "text.primary" }} />
			</IconButton>
		</>
	);
};
